import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import AnimatedModal from '../ui/AnimatedModal';
import {
  XMarkIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import SendEmailToCandidatesModal from '../results/SendEmailToCandidatesModal';
import http from '../../lib/http';
import { DOCUMENT_CATEGORIES } from '../../utils/documentTypes';

/** Shown in template + preview; server uses the same window when sending (see documents.route.js). */
const DOCUMENT_UPLOAD_DEADLINE_TEXT = '180 days';

const GOVT_DOC_LABEL =
  'Govt ID documents (PAN, Aadhaar, Passport, Driving Licence) are not allowed.';

function isGovernmentIdDoc({ type, subtype, label }) {
  return false;
}

function buildEmailHtml({ jobTitle, selectedDocuments }) {
  const list = (selectedDocuments || [])
    .map(
      (d) =>
        `<li style="margin:5px 0;font-size:14px;color:#1f2937;">${String(d.label || '').replace(/</g, '&lt;')}</li>`
    )
    .join('');

  return `<div style="font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif;max-width:600px;margin:0 auto;padding:28px 24px;color:#111827;font-size:14px;line-height:1.65;">
  <p style="margin:0 0 14px 0;">Dear {{Candidate Name}},</p>
  <p style="margin:0 0 14px 0;">
    <strong>Congratulations</strong> — you have been shortlisted for the next steps for the position of
    <strong>{{jobTitle}}</strong> at <strong>{{Client Company Name}}</strong>.
  </p>
  <p style="margin:0 0 22px 0;color:#374151;">
    As part of the next steps, <strong>{{Client Company Name}}</strong> has requested a set of documents for verification.
    This secure request is being facilitated via <strong>TalentCrux</strong> on behalf of <strong>{{Client Company Name}}</strong>.
  </p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:26px 0 10px 0;border-top:1px solid #e5e7eb;padding-top:22px;">Documents required</p>
  <ul style="margin:0 0 20px 22px;padding:0;color:#1f2937;">${list}</ul>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:22px 0 10px 0;">Data security and processing</p>
  <p style="margin:0 0 14px 0;color:#374151;">Your documents will be processed using appropriate technical and organisational safeguards. Access is limited to authorised personnel for recruitment and verification purposes only. Processing is carried out in accordance with applicable law, including the <strong>Digital Personal Data Protection Act, 2023 (India)</strong>, and TalentCrux&rsquo;s obligations as a data processor to the recruiting organisation.</p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:22px 0 10px 0;">Your rights</p>
  <p style="margin:0 0 14px 0;color:#374151;">You may access or request deletion of your submitted documents through your secure link, in line with applicable law and the recruiting organisation&rsquo;s process.</p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:22px 0 10px 0;">Submission timeline</p>
  <p style="margin:0 0 10px 0;color:#374151;">
    We encourage you to upload the requested documents within <strong>2–3 days</strong> of receiving this email to avoid delays.
  </p>
  <p style="margin:0 0 20px 0;color:#374151;">
    secure upload link remains active for <strong>{{X days}}</strong>. For security and privacy, uploaded documents are removed within <strong>180 days</strong> of your submission, unless a shorter retention period is required by <strong>{{Client Company Name}}</strong>.
  </p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:22px 0 10px 0;">Secure submission</p>
  <div style="margin:14px 0 22px 0;">
    <a href="{{UPLOAD_LINK}}" style="background:#1d4ed8;color:#fff;padding:12px 26px;border-radius:4px;text-decoration:none;font-size:14px;font-weight:600;display:inline-block;border:1px solid #1e40af;">Upload documents securely</a>
  </div>
  <p style="margin:0 0 22px 0;color:#374151;">If you require assistance, please contact <a href="mailto:support@talentcrux.ai" style="color:#1d4ed8;text-decoration:none;">support@talentcrux.ai</a>.</p>
  <p style="margin:0;color:#111827;">Regards,<br/><strong>TalentCrux Team</strong><br/><span style="font-size:13px;color:#6b7280;">Secure Document Collection Platform</span></p>
</div>`;
}

const DEFAULT_FOLLOW_UP_HTML = `<div style="font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif;max-width:600px;margin:0 auto;padding:28px 24px;color:#111827;font-size:14px;line-height:1.65;">
  <p style="margin:0 0 14px 0;">Dear {{Candidate Name}},</p>
  <p style="margin:0 0 14px 0;">
    This is a gentle reminder from <strong>{{Client Company Name}}</strong> regarding the pending submission of documents
    for the next steps for <strong>{{jobTitle}}</strong>.
  </p>
  <p style="margin:0 0 22px 0;color:#374151;">
    The secure collection is being facilitated via <strong>TalentCrux</strong> on behalf of <strong>{{Client Company Name}}</strong>.
    Please use the secure link below to review the requested documents and upload them.
  </p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:26px 0 10px 0;border-top:1px solid #e5e7eb;padding-top:22px;">Pending documents</p>
  <p style="margin:0 0 8px 0;color:#374151;">Please refer to your earlier communication for the full list of items requested. If you are unsure, use your secure link below to view requirements and upload files.</p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:22px 0 10px 0;">Data security and processing</p>
  <p style="margin:0 0 14px 0;color:#374151;">All submitted documents are stored and processed with appropriate safeguards. Access is restricted to authorised personnel solely for recruitment and verification purposes, in compliance with applicable data protection law, including the <strong>Digital Personal Data Protection Act, 2023 (India)</strong>.</p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:22px 0 10px 0;">Your rights</p>
  <p style="margin:0 0 14px 0;color:#374151;">You retain the right to access or request deletion of your data through your secure link, subject to applicable law and the recruiting organisation&rsquo;s process.</p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:22px 0 10px 0;">Validity of secure link</p>
  <p style="margin:0 0 12px 0;color:#374151;">
    We encourage you to upload the requested documents within <strong>2–3 days</strong> to avoid delays.
  </p>
  <p style="margin:0 0 20px 0;color:#374151;">
    For security and privacy, uploaded documents are removed within <strong>180 days</strong> of your submission, unless a shorter retention period is required by <strong>{{Client Company Name}}</strong>.
  </p>
  <p style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#374151;margin:22px 0 10px 0;">Secure submission</p>
  <div style="margin:14px 0 22px 0;">
    <a href="{{UPLOAD_LINK}}" style="background:#1d4ed8;color:#fff;padding:12px 26px;border-radius:4px;text-decoration:none;font-size:14px;font-weight:600;display:inline-block;border:1px solid #1e40af;">Upload documents securely</a>
  </div>
  <p style="margin:0 0 22px 0;color:#374151;">For any queries, please contact <a href="mailto:support@talentcrux.ai" style="color:#1d4ed8;text-decoration:none;">support@talentcrux.ai</a>.</p>
  <p style="margin:0;color:#111827;">Regards,<br/><strong>TalentCrux Team</strong><br/><span style="font-size:13px;color:#6b7280;">Secure Document Collection Platform</span></p>
</div>`;

export default function CollectDocumentsModal({
  isOpen,
  onClose,
  onBackToNextSteps,
  selectedCandidates = [],
  screeningId,
  jobPostingId,
  companyName,
  jobTitle,
}) {
  const [stage, setStage] = useState('checklist'); // 'checklist' | 'email'
  const [selectedMap, setSelectedMap] = useState(() => {
    if (!jobPostingId) return {};
    try {
      const saved = localStorage.getItem(`onboarding_doc_config_${jobPostingId}`);
      if (!saved) return {};
      const parsed = JSON.parse(saved);
      const initial = {};
      parsed.forEach((d) => {
        if (d.type !== 'other') {
          initial[`${d.type}::${d.subtype}`] = true;
        }
      });
      return initial;
    } catch {
      return {};
    }
  });
  const [query, setQuery] = useState('');
  const [customDocs, setCustomDocs] = useState(() => {
    if (!jobPostingId) return [];
    try {
      const saved = localStorage.getItem(`onboarding_doc_config_${jobPostingId}`);
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      return parsed
        .filter((d) => d.type === 'other' && d.subtype === 'custom')
        .map((d) => ({ id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, label: d.label }));
    } catch {
      return [];
    }
  }); // array of { id, label }
  const [sending, setSending] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const pickerWrapRef = useRef(null);

  const allDocumentOptions = useMemo(() => {
    const out = [];
    (DOCUMENT_CATEGORIES || [])
      .filter((c) => c.type !== 'other')
      .forEach((cat) => {
        (cat.documents || []).forEach((d) => {
          const opt = { type: cat.type, subtype: d.subtype, label: d.label, categoryLabel: cat.label };
          if (isGovernmentIdDoc(opt)) return;
          out.push(opt);
        });
      });
    out.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return out;
  }, []);

  const filteredOptions = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return allDocumentOptions;
    return allDocumentOptions.filter((o) => String(o.label || '').toLowerCase().includes(q));
  }, [allDocumentOptions, query]);

  const customDocsLowerSet = useMemo(() => {
    return new Set(customDocs.map((d) => String(d.label || '').trim().toLowerCase()).filter(Boolean));
  }, [customDocs]);

  const selectedDocuments = useMemo(() => {
    const docs = [];
    DOCUMENT_CATEGORIES.forEach((cat) => {
      cat.documents.forEach((d) => {
        const key = `${cat.type}::${d.subtype}`;
        if (selectedMap[key]) {
          docs.push({ type: cat.type, subtype: d.subtype, label: d.label });
        }
      });
    });
    customDocs.forEach((d) => {
      if (d?.label) {
        docs.push({ type: 'other', subtype: 'custom', label: d.label });
      }
    });
    return docs;
  }, [selectedMap, customDocs]);

  const selectedCount = selectedDocuments.length;

  const collectDocumentsPlaceholderContext = useMemo(
    () => ({
      documentUploadDeadlineText: DOCUMENT_UPLOAD_DEADLINE_TEXT,
      documentUploadDaysRemainingPreview:
        'the remaining validity period of your secure link (shown at send time)',
      uploadLinkPreview:
        typeof window !== 'undefined'
          ? `${window.location.origin}/documents/upload/your-secure-link`
          : 'https://app.talentcrux.ai/documents/upload/your-secure-link',
    }),
    []
  );

  const closeAll = () => {
    setStage('checklist');
    setSelectedMap({});
    setQuery('');
    setCustomDocs([]);
    setIsPickerOpen(false);
    onClose?.();
  };

  const addCustomDoc = (rawLabel) => {
    const label = String(rawLabel || '').trim();
    if (!label) return;
    if (isGovernmentIdDoc({ type: 'other', subtype: 'custom', label })) {
      toast.error(GOVT_DOC_LABEL);
      return;
    }
    if (customDocsLowerSet.has(label.toLowerCase())) return;
    setCustomDocs((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, label },
    ]);
    setQuery('');
    setIsPickerOpen(false);
  };

  const addOption = (opt) => {
    if (!opt?.type || !opt?.subtype) return;
    const key = `${opt.type}::${opt.subtype}`;
    setSelectedMap((prev) => ({ ...prev, [key]: true }));
    setQuery('');
    setIsPickerOpen(false);
  };

  const removeSelected = (doc) => {
    if (!doc) return;
    if (doc.type === 'other' && doc.subtype === 'custom') {
      setCustomDocs((prev) => prev.filter((d) => d.label !== doc.label));
      return;
    }
    const key = `${doc.type}::${doc.subtype}`;
    setSelectedMap((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleBeforeSend = async (emailData) => {
    setSending(true);
    try {
      const defaultFollowUpHtml = DEFAULT_FOLLOW_UP_HTML;
      const followUpPayload =
        emailData.followUp && emailData.followUp.enabled === false
          ? { enabled: false }
          : {
              enabled: true,
              count: emailData.followUp?.count ?? 1,
              frequency: emailData.followUp?.frequency ?? 3,
              frequencyType: emailData.followUp?.frequencyType || 'days',
              subject:
                emailData.followUp?.subject ||
                'Reminder: Pending document submission — {{jobTitle}}',
              html: emailData.followUp?.html?.trim()
                ? emailData.followUp.html
                : defaultFollowUpHtml,
            };

      await http.post('/api/documents/request', {
        screeningId,
        jobPostingId,
        candidateIds: selectedCandidates.map((c) => c._id || c.id),
        requestedDocuments: selectedDocuments,
        emailSubject: emailData.subject,
        emailBody: emailData.html,
        followUp: followUpPayload,
      });
      toast.success('Document request sent');
    } finally {
      setSending(false);
    }
  };

  const handleBackToNextSteps = () => {
    if (!onBackToNextSteps) return;
    onBackToNextSteps();
  };

  return (
    <>
      <AnimatedModal
        isOpen={isOpen && stage === 'checklist'}
        onClose={closeAll}
        backdropClassName="bg-black/40"
        contentClassName="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[92vh]"
        zIndex={50}
      >
        <div className="flex flex-col min-h-0 flex-1 max-h-[92vh]">
          <div className="flex items-start justify-between flex-shrink-0 px-6 py-5 border-b border-gray-100 bg-white">
            <div className="flex items-start gap-3 min-w-0">
              <div className="p-2.5 rounded-full bg-slate-100 text-slate-700 shrink-0">
                <DocumentTextIcon className="h-5 w-5" />
              </div>
              <div className="space-y-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Document Collection</h3>
                <p className="text-sm text-gray-600 max-w-2xl">
                  Select which documents you want candidates to upload for verification.
                </p>
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 text-amber-700 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold">Not allowed</p>
                    <p className="text-amber-800">{GOVT_DOC_LABEL}</p>
                  </div>
                </div>
                {selectedCandidates.length > 0 && (
                  <div className="inline-flex items-center gap-2 mt-1.5 rounded-full bg-slate-100 px-3 py-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    <span className="text-xs font-medium text-slate-600">
                      {selectedCandidates.length} candidate
                      {selectedCandidates.length !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={closeAll}
              className="text-gray-400 hover:text-gray-600 shrink-0 ml-2"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div
            className={`px-6 py-5 space-y-4 flex-1 min-h-0 overflow-y-auto bg-slate-50/60 ${
              isPickerOpen ? 'pb-56' : ''
            }`}
          >
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Add required documents
              </label>
              <div ref={pickerWrapRef} className="mt-2 relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setIsPickerOpen(true);
                    }}
                    onFocus={() => setIsPickerOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setIsPickerOpen(false);
                      if (e.key === 'Enter') {
                        const q = String(query || '').trim();
                        if (!q) return;
                        // If there is exactly one match, select it; else create custom.
                        if (filteredOptions.length === 1) addOption(filteredOptions[0]);
                        else addCustomDoc(q);
                      }
                    }}
                    placeholder="Search and select (e.g., Degree certificate, Salary slips)…"
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-10 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                  />
                  <button
                    type="button"
                    onClick={() => setIsPickerOpen((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                    aria-label="Toggle document list"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                </div>

                {isPickerOpen && (
                  <div className="absolute z-10 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                    <div className="max-h-72 overflow-y-auto py-1">
                      {filteredOptions.length > 0 ? (
                        filteredOptions.slice(0, 50).map((opt) => {
                          const key = `${opt.type}::${opt.subtype}`;
                          const alreadySelected = !!selectedMap[key];
                          return (
                            <button
                              key={key}
                              type="button"
                              disabled={alreadySelected}
                              onClick={() => addOption(opt)}
                              className={`w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50/60 transition flex items-center justify-between gap-3 ${
                                alreadySelected ? 'text-gray-400 cursor-not-allowed' : 'text-gray-900'
                              }`}
                            >
                              <span className="min-w-0">
                                <span className="font-medium">{opt.label}</span>
                                <span className="ml-2 text-xs text-gray-500">{opt.categoryLabel}</span>
                              </span>
                              {alreadySelected && (
                                <span className="text-[11px] font-semibold text-gray-400 shrink-0">
                                  Added
                                </span>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-3 py-3 text-sm text-gray-600">
                          No matches.
                        </div>
                      )}

                      <div className="border-t border-gray-100 my-1" />
                      <button
                        type="button"
                        onClick={() => addCustomDoc(String(query || '').trim())}
                        className="w-full text-left px-3 py-2.5 text-sm text-gray-900 hover:bg-gray-50 transition"
                        disabled={!String(query || '').trim()}
                      >
                        Create <span className="font-semibold">“{String(query || '').trim() || '…'}”</span> (Other)
                      </button>
                    </div>
                    <div className="px-3 py-2 bg-slate-50 text-[11px] text-gray-600">
                      Tip: press <span className="font-semibold">Enter</span> to add.
                    </div>
                  </div>
                )}
              </div>

              {selectedDocuments.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedDocuments.map((d) => {
                    const key = `${d.type}::${d.subtype}::${d.label}`;
                    return (
                      <span
                        key={key}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-800"
                        title={d.label}
                      >
                        <span className="max-w-[320px] truncate">{d.label}</span>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-700"
                          onClick={() => removeSelected(d)}
                          aria-label={`Remove ${d.label}`}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-3 text-xs text-gray-500">
                  No documents selected yet.
                </p>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-6 py-4 border-t border-gray-100 bg-white">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {onBackToNextSteps && (
                <button
                  type="button"
                  onClick={handleBackToNextSteps}
                  className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
                >
                  Back
                </button>
              )}
              <span className="text-xs text-gray-500">
                {selectedCount} document{selectedCount !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center justify-end gap-2">
              
              <button
                type="button"
                disabled={selectedCount === 0}
                onClick={() => {
                  if (selectedCount === 0) return;
                  setStage('email');
                }}
                className={`px-5 py-2 rounded-md text-sm font-medium text-white shadow-sm transition-all ${
                  selectedCount === 0
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Continue →
              </button>
            </div>
          </div>
        </div>
      </AnimatedModal>

      <SendEmailToCandidatesModal
        isOpen={isOpen && stage === 'email'}
        onClose={() => {
          if (sending) return;
          closeAll();
        }}
        onBack={() => {
          if (sending) return;
          setStage('checklist');
        }}
        nextButtonLabel="Review"
        sendButtonLabel="Send document request"
        selectedCandidates={selectedCandidates}
        screeningId={screeningId}
        roleName={jobTitle}
        companyName={companyName}
        emailContext="collect_documents"
        initialSubject="Document submission required — {{jobTitle}} — {{Company}}"
        initialBodyHtml={buildEmailHtml({ jobTitle, selectedDocuments })}
        extraPlaceholderContext={collectDocumentsPlaceholderContext}
        initialFollowUp={{
          enabled: true,
          count: 1,
          frequency: 3,
          frequencyType: 'days',
          subject: 'Reminder: Pending document submission — {{jobTitle}}',
          html: DEFAULT_FOLLOW_UP_HTML,
        }}
        skipDefaultSend
        onBeforeSend={handleBeforeSend}
        onAfterSendSuccess={async () => {
          closeAll();
        }}
      />
    </>
  );
}

